import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSchoolName } from "./schoolNameStore";
import { ArrowLeft } from "lucide-react";
const ALL_CLASSES = ["UKG", "LKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];
const HS_GROUPS = ["Bio Maths", "Computer Science", "Art & Science", "Commerce"];
const HS_SECTIONS = ['A', 'B'];
function normalizeGroup(name) {
  if (!name) return '';
  const s = String(name).trim().toLowerCase();
  if (!s) return '';
  for (const g of HS_GROUPS) {
    const gl = g.toLowerCase();
    if (s === gl) return g;
    if (s.includes(gl) || gl.includes(s)) return g;
    const sNo = s.replace(/\s+/g, '');
    const glNo = gl.replace(/\s+/g, '');
    if (sNo === glNo || sNo.includes(glNo) || glNo.includes(sNo)) return g;
    const toks = s.split(/\s+/);
    if (toks.some(t => gl.includes(t))) return g;
  }
  return '';
}
const CLASS_FEE_DEFAULTS = {
  UKG: 8500,
  LKG: 8500,
  "Class 1": 9500,
  "Class 2": 9500,
  "Class 3": 9500,
  "Class 4": 9500,
  "Class 5": 9500,
  "Class 6": 12000,
  "Class 7": 12000,
  "Class 8": 12000,
  "Class 9": 14500,
  "Class 10": 14500,
  "Class 11": 18500,
  "Class 12": 18500
};
function getDefaultFee(cls) {
  return CLASS_FEE_DEFAULTS[cls] || 12000;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Stat Card Component (blue theme)
// ─────────────────────────────────────────────────────────────────────────────
function StatCard({
  icon,
  value,
  label,
  trend,
  trendUp,
  gradient
}) {
  return <div style={{
    background: gradient || "linear-gradient(135deg, #0D47A1, #2196F3)",
    borderRadius: 20,
    padding: "20px 18px",
    color: "#fff",
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 4px 12px rgba(13,71,161,0.1)"
  }}>
      <div style={{
      width: 40,
      height: 40,
      borderRadius: 12,
      background: "rgba(255,255,255,0.15)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: 12,
      fontSize: 18
    }}>{icon}</div>
      <div style={{
      fontSize: 24,
      fontWeight: 700,
      lineHeight: 1.2
    }}>{value}</div>
      <div style={{
      fontSize: 11,
      opacity: 0.75,
      marginTop: 6,
      letterSpacing: 0.5,
      textTransform: "uppercase"
    }}>{label}</div>
      {trend && <div style={{
      position: "absolute",
      top: 14,
      right: 14,
      background: "rgba(255,255,255,0.15)",
      borderRadius: 20,
      padding: "2px 8px",
      fontSize: 11,
      fontWeight: 600,
      color: trendUp ? "#90CAF9" : "#90CAF9"
    }}>{trend}</div>}
    </div>;
}

// ─────────────────────────────────────────────────────────────────────────────
//  Group color map for senior class cards (blue theme)
// ─────────────────────────────────────────────────────────────────────────────
const GROUP_STYLES = {
  "Bio Maths": {
    border: "#90CAF9",
    bg: "#E3F2FD",
    badge: "#0D47A1",
    badgeBg: "#E3F2FD",
    icon: "fas fa-dna"
  },
  "Computer Science": {
    border: "#90CAF9",
    bg: "#E3F2FD",
    badge: "#0D47A1",
    badgeBg: "#E3F2FD",
    icon: "fas fa-laptop-code"
  },
  "Art & Science": {
    border: "#90CAF9",
    bg: "#E3F2FD",
    badge: "#0D47A1",
    badgeBg: "#E3F2FD",
    icon: "fas fa-palette"
  },
  "Commerce": {
    border: "#90CAF9",
    bg: "#E3F2FD",
    badge: "#0D47A1",
    badgeBg: "#E3F2FD",
    icon: "fas fa-chart-line"
  }
};

// ─────────────────────────────────────────────────────────────────────────────
//  Fee component row helper — turns a term's `components` / `componentsPaid`
//  into a flat, independently-payable list: School Fee, Tuition Fee, Van Fee,
//  Book Fee, and each "Others" entry. Rows with no due amount set are hidden.
// ─────────────────────────────────────────────────────────────────────────────
function getComponentRows(student, termKey) {
  if (!student || !student.termFees || !student.termFees[termKey]) return [];
  const tf = student.termFees[termKey];
  const comps = tf.components || {};
  const compsPaid = tf.componentsPaid || {};
  const rows = [{
    key: 'schoolFee',
    label: 'School Fee',
    due: Number(comps.schoolFee) || 0,
    paid: Number(compsPaid.schoolFee) || 0
  }, {
    key: 'tuitionFee',
    label: 'Tuition Fee',
    due: Number(comps.tuitionFee) || 0,
    paid: Number(compsPaid.tuitionFee) || 0
  }, {
    key: 'vanFee',
    label: 'Van Fee',
    due: Number(comps.vanFee) || 0,
    paid: Number(compsPaid.vanFee) || 0
  }, {
    key: 'bookFee',
    label: 'Book Fee',
    due: Number(comps.bookFee) || 0,
    paid: Number(compsPaid.bookFee) || 0
  }];
  (Array.isArray(comps.others) ? comps.others : []).forEach(o => {
    const paidEntry = (Array.isArray(compsPaid.others) ? compsPaid.others : []).find(x => x.label === o.label);
    rows.push({
      key: 'other',
      label: o.label,
      due: Number(o.amount) || 0,
      paid: paidEntry ? Number(paidEntry.amount) || 0 : 0
    });
  });
  return rows.filter(r => r.due > 0);
}

// ─────────────────────────────────────────────────────────────────────────────
//  MAIN FEE COMPONENT (blue theme, Poppins, Font Awesome, responsive)
// ─────────────────────────────────────────────────────────────────────────────
export default function Fee() {
  const schoolName = useSchoolName();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [modalStudent, setModalStudent] = useState(null);
  const [selClass, setSelClass] = useState(null);
  const [selSection, setSelSection] = useState(null);
  const [selGroup, setSelGroup] = useState(null);
  const [allStudents, setAllStudents] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [showBulkFeeModal, setShowBulkFeeModal] = useState(false);
  const [compSchoolFee, setCompSchoolFee] = useState('');
  const [compVanFee, setCompVanFee] = useState('');
  const [compHostelFee, setCompHostelFee] = useState('');
  const [compBookFee, setCompBookFee] = useState('');
  const [compTuitionFee, setCompTuitionFee] = useState('');
  const [compOthers, setCompOthers] = useState([]);
  const [otherLabel, setOtherLabel] = useState('');
  const [otherAmount, setOtherAmount] = useState('');
  const [applyScope, setApplyScope] = useState('class');

  // ── Per-component ("School Fee / Van Fee / Book Fee / Tuition Fee / Others")
  //    payment modal state ──
  const [termPaymentModal, setTermPaymentModal] = useState(null); // { studentId, termKey }
  const [paymentInputs, setPaymentInputs] = useState({});
  function openBulkModal(scope = 'class') {
    setApplyScope(scope);
    const list = allStudents.filter(s => s.cls === selClass && (scope !== 'group' || !selGroup || s.group === selGroup));
    if (list.length > 0) {
      const sample = list[0];
      const t1 = sample.termFees && sample.termFees.T1 && sample.termFees.T1.components ? sample.termFees.T1.components : {
        schoolFee: 0,
        vanFee: 0,
        bookFee: 0,
        tuitionFee: 0,
        others: []
      };
      const t2 = sample.termFees && sample.termFees.T2 && sample.termFees.T2.components ? sample.termFees.T2.components : {
        schoolFee: 0,
        vanFee: 0,
        bookFee: 0,
        tuitionFee: 0,
        others: []
      };
      const t3 = sample.termFees && sample.termFees.T3 && sample.termFees.T3.components ? sample.termFees.T3.components : {
        schoolFee: 0,
        vanFee: 0,
        bookFee: 0,
        tuitionFee: 0,
        others: []
      };
      const sum = k => Number(t1 && t1[k] || 0) + Number(t2 && t2[k] || 0) + Number(t3 && t3[k] || 0);
      setCompSchoolFee(sum('schoolFee') ? String(sum('schoolFee')) : '');
      setCompTuitionFee(sum('tuitionFee') ? String(sum('tuitionFee')) : '');
      setCompVanFee(sum('vanFee') ? String(sum('vanFee')) : '');
      setCompBookFee(sum('bookFee') ? String(sum('bookFee')) : '');
      const othersMap = {};
      [...(t1.others || []), ...(t2.others || []), ...(t3.others || [])].forEach(o => {
        if (o && o.label) {
          othersMap[o.label] = (othersMap[o.label] || 0) + (Number(o.amount) || 0);
        }
      });
      // "Hostel Fee" is auto-added for Hostel Students (see submit handler below) —
      // pull it out into its own field instead of showing it as a generic "other".
      setCompHostelFee(othersMap['Hostel Fee'] ? String(othersMap['Hostel Fee']) : '');
      delete othersMap['Hostel Fee'];
      const others = Object.keys(othersMap).map(k => ({
        label: k,
        amount: othersMap[k]
      }));
      setCompOthers(others);
    } else {
      setCompSchoolFee('');
      setCompTuitionFee('');
      setCompVanFee('');
      setCompHostelFee('');
      setCompBookFee('');
      setCompOthers([]);
    }
    setShowBulkFeeModal(true);
  }
  async function loadAdmissions() {
    try {
      const res = await fetch('https://schoolcrm-fzcv.onrender.com/api/admissions');
      if (!res.ok) throw new Error('Failed to fetch admissions');
      const data = await res.json();
      const counters = {};
      const students = data.map(adm => {
        const cls = adm.applyingClass || 'Class 1';
        const section = adm.section || 'A';
        const key = `${cls}::${section}`;
        counters[key] = (counters[key] || 0) + 1;
        const roll = `${counters[key]}`.padStart(2, '0');
        const totalFee = typeof adm.totalFee === 'number' && adm.totalFee > 0 ? adm.totalFee : getDefaultFee(cls);
        let termFees;
        if (adm.termFees && adm.termFees.T1) {
          const nf = tf => ({
            due: tf.due || 0,
            paid: tf.paid || 0,
            pending: typeof tf.pending === 'number' ? tf.pending : Math.max(0, (tf.due || 0) - (tf.paid || 0)),
            components: tf.components || {
              schoolFee: 0,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            },
            componentsPaid: tf.componentsPaid || {
              schoolFee: 0,
              vanFee: 0,
              bookFee: 0,
              tuitionFee: 0,
              others: []
            }
          });
          termFees = {
            T1: nf(adm.termFees.T1),
            T2: nf(adm.termFees.T2),
            T3: nf(adm.termFees.T3)
          };
        } else {
          const t1 = Math.ceil(totalFee / 3);
          const t2 = Math.floor((totalFee - t1) / 2);
          const t3 = totalFee - t1 - t2;
          termFees = {
            T1: {
              due: t1,
              paid: 0,
              pending: t1,
              components: {
                schoolFee: t1,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              },
              componentsPaid: {
                schoolFee: 0,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              }
            },
            T2: {
              due: t2,
              paid: 0,
              pending: t2,
              components: {
                schoolFee: t2,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              },
              componentsPaid: {
                schoolFee: 0,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              }
            },
            T3: {
              due: t3,
              paid: 0,
              pending: t3,
              components: {
                schoolFee: t3,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              },
              componentsPaid: {
                schoolFee: 0,
                vanFee: 0,
                bookFee: 0,
                tuitionFee: 0,
                others: []
              }
            }
          };
        }
        const rawGroup = adm.group || '';
        const mapped = normalizeGroup(rawGroup);
        const group = mapped || (cls === "Class 11" || cls === "Class 12" ? HS_GROUPS[0] : '');
        return {
          id: adm._id || `${cls.replace(/\s+/g, '')}-${roll}`,
          name: adm.studentName || 'Unknown',
          roll,
          cls,
          section,
          group,
          totalFee,
          termFees,
          paid: (termFees.T1.paid || 0) + (termFees.T2.paid || 0) + (termFees.T3.paid || 0),
          pending: typeof adm.pending === 'number' ? adm.pending : totalFee - ((termFees.T1.paid || 0) + (termFees.T2.paid || 0) + (termFees.T3.paid || 0)),
          pendingTerm: typeof adm.pending === 'number' ? adm.pending : totalFee - ((termFees.T1.paid || 0) + (termFees.T2.paid || 0) + (termFees.T3.paid || 0)),
          lastPayment: adm.lastPayment || '—',
          admissionStatus: adm.status || 'Enquiry',
          raw: adm
        };
      });
      setAllStudents(students);
      setTransactions([]);
    } catch (err) {
      console.error('Failed to load admissions for fees:', err);
    }
  }
  useEffect(() => {
    loadAdmissions();
  }, []);
  const totals = useMemo(() => {
    const totalStudents = allStudents.length;
    const totalFees = allStudents.reduce((s, x) => s + x.totalFee, 0);
    const totalCollected = allStudents.reduce((s, x) => s + x.paid, 0);
    const totalPending = allStudents.reduce((s, x) => s + x.pendingTerm, 0);
    return {
      totalStudents,
      totalFees,
      totalCollected,
      totalPending
    };
  }, [allStudents]);
  function updateStudent(updated) {
    setAllStudents(prev => prev.map(s => s.id === updated.id ? updated : s));
  }
  function appendTransaction(tx) {
    setTransactions(prev => [tx, ...prev]);
  }
  async function openStudent(student) {
    try {
      const res = await fetch(`https://schoolcrm-fzcv.onrender.com/api/fees/student/${student.id}`);
      let fees = [];
      if (res.ok) {
        fees = await res.json();
      }
      const feeTx = fees.map(f => ({
        id: f._id,
        studentId: student.id,
        amount: f.amount,
        term: f.term || 'OTHER',
        method: f.method || 'cash',
        note: f.componentLabel || f.note || '',
        status: 'success',
        dateTime: new Date(f.createdAt).toLocaleString()
      }));
      setTransactions(prev => [...feeTx, ...prev.filter(t => t.studentId !== student.id)]);
      setModalStudent(student);
    } catch (err) {
      console.error('Failed to fetch fees for student', err);
      setModalStudent(student);
    }
  }

  // ── Open the per-component payment modal for a term, enforcing the same
  //    sequential T1 → T2 → T3 rule the old whole-term "Pay" button used. ──
  function openTermPaymentModal(id, termKey) {
    const student = allStudents.find(s => s.id === id);
    if (!student) return;
    if (termKey === 'T2') {
      const t1 = student.termFees.T1;
      if (t1.pending > 0) {
        return alert(`Cannot collect payment for Term 2 because Term 1 has pending dues of ₹${t1.pending.toLocaleString()}. Please complete Term 1 payment first.`);
      }
    }
    if (termKey === 'T3') {
      const t1 = student.termFees.T1;
      const t2 = student.termFees.T2;
      if (t1.pending > 0) {
        return alert(`Cannot collect payment for Term 3 because Term 1 has pending dues of ₹${t1.pending.toLocaleString()}. Please complete Term 1 payment first.`);
      }
      if (t2.pending > 0) {
        return alert(`Cannot collect payment for Term 3 because Term 2 has pending dues of ₹${t2.pending.toLocaleString()}. Please complete Term 2 payment first.`);
      }
    }
    setTermPaymentModal({
      studentId: id,
      termKey
    });
  }

  // ── Pay a single fee-component row (School Fee / Van Fee / Book Fee /
  //    Tuition Fee / one "Others" item) inside the open term payment modal. ──
  async function payComponent(row) {
    if (!termPaymentModal) return;
    const {
      studentId,
      termKey
    } = termPaymentModal;
    const student = allStudents.find(s => s.id === studentId);
    if (!student) return;
    const pending = Math.max(0, row.due - row.paid);
    if (pending <= 0) return;
    const inputKey = `${termKey}-${row.key}-${row.label}`;
    const raw = paymentInputs[inputKey];
    const amt = parseInt(String(raw == null || raw === '' ? pending : raw).replace(/[^0-9]/g, ''), 10) || 0;
    if (amt <= 0) return alert('Enter a valid amount');
    try {
      const res = await fetch('https://schoolcrm-fzcv.onrender.com/api/fees/collect-component', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId,
          termKey,
          componentKey: row.key,
          otherLabel: row.key === 'other' ? row.label : undefined,
          amount: amt
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to record payment');
      const adm = data.admission;
      const updated = {
        ...student
      };
      updated.totalFee = typeof adm.totalFee === 'number' ? adm.totalFee : updated.totalFee;
      updated.termFees = adm.termFees || updated.termFees;
      updated.paid = typeof adm.paid === 'number' ? adm.paid : updated.paid;
      updated.pending = typeof adm.pending === 'number' ? adm.pending : updated.pending;
      updated.pendingTerm = typeof adm.pendingTerm === 'number' ? adm.pendingTerm : updated.pendingTerm;
      updated.lastPayment = adm.lastPayment || updated.lastPayment;
      updateStudent(updated);
      appendTransaction({
        id: data.fee._id || `TX${Date.now()}`,
        studentId,
        name: student.name,
        class: student.cls,
        amount: data.fee.amount || amt,
        term: termKey,
        method: 'cash',
        note: data.fee.componentLabel || row.label,
        status: 'success',
        dateTime: new Date(data.fee.createdAt).toLocaleString()
      });
      setPaymentInputs(prev => ({
        ...prev,
        [inputKey]: ''
      }));
      await loadAdmissions();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to record payment. See console for details.');
    }
  }
  function handleEditTermDue(id, termKey) {
    const student = allStudents.find(s => s.id === id);
    if (!student) return;
    const term = student.termFees[termKey];
    const comps = term.components || {
      schoolFee: 0,
      vanFee: 0,
      bookFee: 0,
      tuitionFee: 0,
      others: []
    };
    const schoolInp = window.prompt(`Enter School Fee for ${student.name} (${termKey})`, `${comps.schoolFee || 0}`);
    if (schoolInp === null) return;
    const schoolFee = parseInt(schoolInp.replace(/[^0-9]/g, ''), 10) || 0;
    const tuitionInp = window.prompt(`Enter Tuition Fee for ${student.name} (${termKey})`, `${comps.tuitionFee || 0}`);
    if (tuitionInp === null) return;
    const tuitionFee = parseInt(tuitionInp.replace(/[^0-9]/g, ''), 10) || 0;
    const vanInp = window.prompt(`Enter Van Fee for ${student.name} (${termKey})`, `${comps.vanFee || 0}`);
    if (vanInp === null) return;
    const vanFee = parseInt(vanInp.replace(/[^0-9]/g, ''), 10) || 0;
    const bookInp = window.prompt(`Enter Book Fee for ${student.name} (${termKey})`, `${comps.bookFee || 0}`);
    if (bookInp === null) return;
    const bookFee = parseInt(bookInp.replace(/[^0-9]/g, ''), 10) || 0;
    const othersSumCurrent = Array.isArray(comps.others) ? comps.others.reduce((a, x) => a + (x.amount || 0), 0) : 0;
    const othersInp = window.prompt(`Enter Others Fee for ${student.name} (${termKey})`, `${othersSumCurrent}`);
    if (othersInp === null) return;
    const othersVal = parseInt(othersInp.replace(/[^0-9]/g, ''), 10) || 0;
    const others = othersVal > 0 ? [{
      label: 'Others',
      amount: othersVal
    }] : [];
    const payload = {
      studentId: student.id,
      termKey,
      components: {
        schoolFee,
        tuitionFee,
        vanFee,
        bookFee,
        others
      }
    };
    (async () => {
      try {
        const res = await fetch('https://schoolcrm-fzcv.onrender.com/api/fees/edit-term-due', {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('Failed to update term due');
        const adm = await res.json();
        const updated = {
          ...student
        };
        updated.totalFee = adm.totalFee || updated.totalFee;
        updated.termFees = adm.termFees || updated.termFees;
        updated.paid = adm.paid || updated.paid;
        updated.pending = adm.pending || updated.pending;
        updated.pendingTerm = adm.pendingTerm || updated.pendingTerm;
        updateStudent(updated);
        await loadAdmissions();
      } catch (err) {
        console.error(err);
        alert('Failed to update term due. See console for details.');
      }
    })();
  }
  const displayedStudents = selClass && (selSection || selGroup) ? allStudents.filter(s => {
    if (s.cls !== selClass) return false;
    if (selSection && selSection !== 'ALL' && s.section !== selSection) return false;
    if ((selClass === 'Class 11' || selClass === 'Class 12') && selGroup && selGroup !== 'ALL' && s.group !== selGroup) return false;
    if (search && !(s.name.toLowerCase().includes(search.toLowerCase()) || s.roll.includes(search))) return false;
    return true;
  }) : [];

  // ── Senior class group section UI (blue theme) ──
  function SeniorGroupView() {
    const isSenior = selClass === 'Class 11' || selClass === 'Class 12';
    if (!isSenior) return null;
    if (!selGroup) {
      return <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 20,
        marginBottom: 24
      }}>
          {HS_GROUPS.map(g => {
          const gs = GROUP_STYLES[g] || {
            border: '#90CAF9',
            bg: '#E3F2FD',
            badge: '#0D47A1',
            badgeBg: '#E3F2FD',
            icon: 'fas fa-folder-open'
          };
          const list = allStudents.filter(s => s.cls === selClass && s.group === g);
          const pending = list.reduce((a, x) => a + x.pendingTerm, 0);
          const collected = list.reduce((a, x) => a + x.paid, 0);
          const total = pending + collected;
          const pct = total > 0 ? Math.round(collected / total * 100) : 0;
          return <div key={g} style={{
            background: gs.bg,
            borderRadius: 24,
            padding: '24px 16px 20px',
            textAlign: 'center',
            transition: 'transform 0.2s, box-shadow 0.2s',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(13,71,161,0.06)'
          }} onClick={() => setSelGroup(g)}>
                <div style={{
              width: 64,
              height: 64,
              margin: '0 auto 12px',
              background: gs.badgeBg,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 28,
              color: gs.badge,
              boxShadow: `0 4px 10px rgba(13,71,161,0.08)`
            }}>
                  <i className={gs.icon}></i>
                </div>

                <div style={{
              fontSize: 22,
              fontWeight: 800,
              color: '#0D47A1',
              marginBottom: 4
            }}>
                  ₹{pending.toLocaleString()}
                </div>
                <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: '#2196F3',
              marginBottom: 12
            }}>
                  ₹{collected.toLocaleString()}
                </div>

                <div style={{
              marginBottom: 16,
              padding: '0 8px'
            }}>
                  <div style={{
                height: 6,
                background: '#E3F2FD',
                borderRadius: 99,
                overflow: 'hidden'
              }}>
                    <div style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: gs.badge,
                  borderRadius: 99
                }} />
                  </div>
                  <div style={{
                fontSize: 10,
                color: '#2196F3',
                marginTop: 5
              }}>{pct}% collected</div>
                </div>

                <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '50%',
              background: '#fff',
              color: gs.badge,
              fontSize: 14,
              transition: 'all 0.2s',
              boxShadow: '0 2px 6px rgba(13,71,161,0.08)'
            }}>
                  <i className="fas fa-arrow-right"></i>
                </div>
              </div>;
        })}
        </div>;
    }
    const gs = GROUP_STYLES[selGroup] || {
      border: '#90CAF9',
      bg: '#E3F2FD',
      badge: '#0D47A1',
      badgeBg: '#E3F2FD',
      icon: 'fas fa-folder-open'
    };
    const groupStudents = allStudents.filter(s => s.cls === selClass && s.group === selGroup);
    const groupPending = groupStudents.reduce((a, x) => a + x.pendingTerm, 0);
    const groupCollected = groupStudents.reduce((a, x) => a + x.paid, 0);
    return <div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
        flexWrap: 'wrap',
        gap: 10
      }}>
          <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
            <button onClick={() => setSelGroup(null)} style={{
            fontSize: 12,
            background: '#E3F2FD',
            border: 'none',
            borderRadius: 40,
            padding: '6px 14px',
            cursor: 'pointer',
            fontWeight: 500,
            color: '#0D47A1'
          }}>
              <ArrowLeft className="inline w-[1em] h-[1em] align-[-0.125em]" /> Back to Groups
            </button>
            <span style={{
            fontSize: 14,
            fontWeight: 700,
            color: '#0D47A1'
          }}>
              <i className={gs.icon} style={{
              marginRight: 6
            }}></i> {selGroup}
            </span>
          </div>
          <span style={{
          fontSize: 12,
          color: '#2196F3'
        }}>
            {groupStudents.length} {groupStudents.length === 1 ? 'student' : 'students'} in {selGroup}
          </span>
        </div>

        <div style={{
        background: gs.bg,
        borderRadius: 20,
        padding: '20px 24px',
        marginBottom: 20,
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        boxShadow: '0 2px 8px rgba(13,71,161,0.04)'
      }}>
          <div>
            <div style={{
            fontSize: 18,
            fontWeight: 800,
            color: '#0D47A1',
            marginBottom: 4
          }}>
              <i className={gs.icon} style={{
              marginRight: 6
            }}></i> {selGroup} · {selClass}
            </div>
            <div style={{
            display: 'flex',
            gap: 24,
            flexWrap: 'wrap'
          }}>
              <div>
                <div style={{
                fontSize: 11,
                color: '#2196F3'
              }}>Pending</div>
                <div style={{
                fontSize: 16,
                fontWeight: 800,
                color: '#0D47A1'
              }}>₹{groupPending.toLocaleString()}</div>
              </div>
              <div>
                <div style={{
                fontSize: 11,
                color: '#2196F3'
              }}>Collected</div>
                <div style={{
                fontSize: 16,
                fontWeight: 800,
                color: '#2196F3'
              }}>₹{groupCollected.toLocaleString()}</div>
              </div>
              <div>
                <div style={{
                fontSize: 11,
                color: '#2196F3'
              }}>Students</div>
                <div style={{
                fontSize: 16,
                fontWeight: 800,
                color: '#0D47A1'
              }}>{groupStudents.length}</div>
              </div>
            </div>
          </div>
          <div style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap'
        }}>
            <button onClick={() => setSelSection('ALL')} style={{
            background: '#0D47A1',
            color: '#fff',
            border: 'none',
            borderRadius: 40,
            padding: '8px 20px',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer'
          }}>
              View All Students
            </button>
            <button onClick={() => setSelSection(HS_SECTIONS[0])} style={{
            background: '#fff',
            color: '#0D47A1',
            borderRadius: 40,
            padding: '8px 20px',
            fontWeight: 600,
            fontSize: 12,
            cursor: 'pointer',
            boxShadow: '0 2px 6px rgba(13,71,161,0.08)'
          }}>
              Open Section A
            </button>
          </div>
        </div>

        <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: 16
      }}>
          {HS_SECTIONS.map(sec => {
          const list = allStudents.filter(s => s.cls === selClass && s.section === sec && s.group === selGroup);
          const pending = list.reduce((a, x) => a + x.pendingTerm, 0);
          const collected = list.reduce((a, x) => a + x.paid, 0);
          return <div key={sec} style={{
            background: '#fff',
            borderRadius: 16,
            padding: '18px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(13,71,161,0.04)'
          }}>
                <div style={{
              fontSize: 15,
              fontWeight: 800,
              color: '#0D47A1',
              marginBottom: 4
            }}>Section {sec}</div>
                <div style={{
              fontSize: 11,
              color: '#2196F3',
              marginBottom: 12
            }}>
                  {list.length} {list.length === 1 ? 'student' : 'students'}
                </div>
                <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              marginBottom: 16
            }}>
                  <div>
                    <div style={{
                  fontSize: 10,
                  color: '#2196F3'
                }}>Pending</div>
                    <div style={{
                  fontSize: 14,
                  fontWeight: 800,
                  color: '#0D47A1'
                }}>₹{pending.toLocaleString()}</div>
                  </div>
                  <div>
                    <div style={{
                  fontSize: 10,
                  color: '#2196F3'
                }}>Collected</div>
                    <div style={{
                  fontSize: 14,
                  fontWeight: 700,
                  color: '#2196F3'
                }}>₹{collected.toLocaleString()}</div>
                  </div>
                </div>
                <button onClick={() => setSelSection(sec)} style={{
              width: '100%',
              padding: '8px 0',
              borderRadius: 40,
              background: gs.badgeBg,
              color: gs.badge,
              border: 'none',
              fontWeight: 600,
              fontSize: 12,
              cursor: 'pointer'
            }}>
                  Open Section {sec}
                </button>
              </div>;
        })}
        </div>
      </div>;
  }
  return <>
    <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" rel="stylesheet" />
    <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
    <style>{`
      @media print {
        body * { visibility: hidden; }
        .fee-print-area, .fee-print-area * { visibility: visible; }
        .fee-print-area {
          position: absolute; left: 0; top: 0; width: 100%;
          box-shadow: none !important;
        }
        .print-only-heading { display: block !important; }
      }
    `}</style>

    <div style={{
      fontFamily: "'Poppins', sans-serif",
      background: "#FFFFFF",
      minHeight: "100vh",
      padding: "20px 24px"
    }}>
      {/* Header with School Name & Back Button */}
      <div style={{
        background: "linear-gradient(135deg, #0D47A1, #2196F3)",
        padding: "16px 24px",
        borderRadius: 20,
        marginBottom: 24,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 16,
        boxShadow: "0 8px 20px rgba(13,71,161,0.08)"
      }}>
        <div style={{
          display: 'flex',
          gap: 16,
          alignItems: 'center'
        }}>
          <div style={{
  width: 50,
  height: 50,
  borderRadius: 14,
  background: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden' // This ensures the image stays within the rounded corners
}}>
  <img 
    src="/school-logo.png" 
    alt="School Logo"
    style={{
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    }}
  />
</div>
          <div>
            <div style={{
              fontWeight: 800,
              fontSize: 20,
              color: "#FFFFFF"
            }}>{schoolName} · Fee Ledger</div>
            <div style={{
              fontSize: 12,
              color: "#90CAF9",
              marginTop: 4
            }}>Class & Section breakdown · Term-wise dues</div>
          </div>
        </div>
        <div style={{
          display: "flex",
          gap: 12
        }}>
         
          <button onClick={() => navigate(-1)} style={{
            background: "#ffffff",
            color: "#0D47A1",
            border: "none",
            padding: "8px 16px",
            borderRadius: 40,
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
            display: "flex",
            alignItems: "center",
            gap: 6
          }}><i className="fas fa-arrow-left"></i> Back</button>
        </div>
      </div>

      {/* Top Stats */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 16,
        marginBottom: 24
      }}>
        <StatCard icon={<i className="fas fa-users"></i>} value={totals.totalStudents} label="Total Students" gradient="linear-gradient(135deg, #0D47A1, #2196F3)" />
        <StatCard icon={<i className="fas fa-rupee-sign"></i>} value={`₹${totals.totalCollected.toLocaleString()}`} label="Collected" gradient="linear-gradient(135deg, #2196F3, #0D47A1)" />
        <StatCard icon={<i className="fas fa-hourglass-half"></i>} value={`₹${totals.totalPending.toLocaleString()}`} label="Pending Term Fees" gradient="linear-gradient(135deg, #0D47A1, #2196F3)" />
        <StatCard icon={<i className="fas fa-file-invoice-dollar"></i>} value={`₹${totals.totalFees.toLocaleString()}`} label="Total Fee Liability" gradient="linear-gradient(135deg, #2196F3, #0D47A1)" />
      </div>

      {/* ── Class overview grid ── */}
      {selClass === null ? <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: 16
      }}>
          {ALL_CLASSES.map(c => {
          const clsStudents = allStudents.filter(s => s.cls === c);
          const pending = clsStudents.reduce((a, x) => a + x.pendingTerm, 0);
          return <div key={c} style={{
            background: "#FFFFFF",
            borderRadius: 20,
            padding: "20px 16px",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
          }}>
                <div style={{
              fontSize: 16,
              fontWeight: 700,
              color: "#0D47A1"
            }}>{c}</div>
                <div style={{
              fontSize: 12,
              color: "#2196F3",
              marginTop: 4
            }}>
                  <i className="fas fa-user-graduate"></i> {clsStudents.length} {clsStudents.length === 1 ? 'student' : 'students'}
                </div>
                <div style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#0D47A1",
              marginTop: 12
            }}>₹{pending.toLocaleString()}</div>
                <button onClick={() => {
              setSelClass(c);
              setSelSection(null);
              setSelGroup(null);
            }} style={{
              marginTop: 16,
              width: "100%",
              background: "#E3F2FD",
              border: "none",
              borderRadius: 40,
              padding: "8px 0",
              color: "#0D47A1",
              fontWeight: 600,
              fontSize: 12,
              cursor: "pointer"
            }}>View Sections <i className="fas fa-chevron-right"></i></button>
              </div>;
        })}
        </div> : selSection === null ? <div style={{
        marginBottom: 24
      }}>
          <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 20,
          flexWrap: "wrap",
          gap: 12
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            gap: 12
          }}>
              <button onClick={() => setSelClass(null)} style={{
              background: "#E3F2FD",
              border: "none",
              borderRadius: 40,
              padding: "6px 14px",
              fontSize: 12,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              color: "#0D47A1"
            }}><i className="fas fa-arrow-left"></i> All Classes</button>
              <button onClick={() => setSelSection('ALL')} style={{
              background: "#E3F2FD",
              border: "none",
              borderRadius: 40,
              padding: "6px 14px",
              fontSize: 12,
              cursor: "pointer",
              color: "#0D47A1"
            }}>Open All Students</button>
              <button onClick={() => openBulkModal('class')} style={{
              background: "#0D47A1",
              color: "#FFFFFF",
              border: "none",
              borderRadius: 40,
              padding: "6px 14px",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer"
            }}><i className="fas fa-layer-group"></i> Set Class Fees</button>
            </div>
            <div style={{
            fontSize: 12,
            color: "#2196F3"
          }}>
              {allStudents.filter(s => s.cls === selClass).length} students in {selClass}
            </div>
          </div>

          {/* Class-level summary cards */}
          {(() => {
          const clsStudents = allStudents.filter(s => s.cls === selClass);
          const collected = clsStudents.reduce((a, x) => a + x.paid, 0);
          const pending = clsStudents.reduce((a, x) => a + x.pendingTerm, 0);
          const completed = clsStudents.filter(s => s.pendingTerm === 0).length;
          return <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 16,
            marginBottom: 24
          }}>
                <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: "14px 16px",
              boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
            }}>
                  <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}><i className="fas fa-coins"></i> Collected</div>
                  <div style={{
                fontSize: 18,
                fontWeight: 700,
                marginTop: 6,
                color: "#0D47A1"
              }}>₹{collected.toLocaleString()}</div>
                </div>
                <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: "14px 16px",
              boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
            }}>
                  <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}><i className="fas fa-hourglass-half"></i> Pending</div>
                  <div style={{
                fontSize: 18,
                fontWeight: 700,
                marginTop: 6,
                color: "#0D47A1"
              }}>₹{pending.toLocaleString()}</div>
                </div>
                <div style={{
              background: "#FFFFFF",
              borderRadius: 16,
              padding: "14px 16px",
              boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
            }}>
                  <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}><i className="fas fa-check-circle"></i> Completed (no dues)</div>
                  <div style={{
                fontSize: 18,
                fontWeight: 700,
                marginTop: 6,
                color: "#0D47A1"
              }}>{completed}</div>
                </div>
              </div>;
        })()}

          {/* Senior vs Junior section view */}
          {selClass === 'Class 11' || selClass === 'Class 12' ? <SeniorGroupView /> : <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 16
        }}>
              {(selClass === 'UKG' || selClass === 'LKG' ? ['A'] : ['A', 'B', 'C']).map(sec => {
            const list = allStudents.filter(s => s.cls === selClass && s.section === sec);
            const pending = list.reduce((a, x) => a + x.pendingTerm, 0);
            return <div key={sec} style={{
              background: "#FFFFFF",
              borderRadius: 20,
              padding: "20px 16px",
              textAlign: "center",
              boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
            }}>
                    <div style={{
                fontSize: 16,
                fontWeight: 700,
                color: "#0D47A1"
              }}>Section {sec}</div>
                    <div style={{
                fontSize: 12,
                color: "#2196F3",
                marginTop: 4
              }}>{list.length} students</div>
                    <div style={{
                fontSize: 20,
                fontWeight: 800,
                color: "#0D47A1",
                marginTop: 12
              }}>₹{pending.toLocaleString()}</div>
                    <button onClick={() => setSelSection(sec)} style={{
                marginTop: 16,
                width: "100%",
                background: "#E3F2FD",
                border: "none",
                borderRadius: 40,
                padding: "8px 0",
                color: "#0D47A1",
                fontWeight: 600,
                fontSize: 12,
                cursor: "pointer"
              }}>Open Section <i className="fas fa-arrow-right"></i></button>
                  </div>;
          })}
            </div>}
        </div> : null}

      {/* ── Student table ── */}
      <div style={{
        display: selClass && selSection ? 'block' : 'none'
      }}>
        <div style={{
          background: "#FFFFFF",
          borderRadius: 24,
          padding: "20px",
          marginBottom: 24,
          boxShadow: "0 2px 8px rgba(13,71,161,0.04)"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 20,
            flexWrap: "wrap",
            gap: 12
          }}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap"
            }}>
              <button onClick={() => selSection === 'ALL' ? setSelClass(null) : setSelSection(null)} style={{
                background: "#E3F2FD",
                border: "none",
                borderRadius: 40,
                padding: "6px 14px",
                fontSize: 12,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "#0D47A1"
              }}><i className="fas fa-arrow-left"></i> Back</button>
              <div style={{
                position: "relative"
              }}>
                <i className="fas fa-search" style={{
                  position: "absolute",
                  left: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#2196F3",
                  fontSize: 12
                }}></i>
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search student, roll or name" style={{
                  background: "#FFFFFF",
                  borderRadius: 40,
                  padding: "8px 16px 8px 36px",
                  fontSize: 12,
                  outline: "none",
                  width: 240,
                  boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
                }} />
              </div>
              <div style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap"
              }}>
                <span style={{
                  fontSize: 12,
                  fontWeight: 700,
                  background: "#E3F2FD",
                  color: "#0D47A1",
                  padding: "6px 12px",
                  borderRadius: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}>
                  <i className="fas fa-graduation-cap"></i> {selClass}
                </span>
                {selGroup && selGroup !== 'ALL' && <span style={{
                  fontSize: 12,
                  fontWeight: 700,
                  background: "#E3F2FD",
                  color: "#0D47A1",
                  padding: "6px 12px",
                  borderRadius: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4
                }}>
                    <i className="fas fa-layer-group"></i> {selGroup}
                  </span>}
                {selSection && selSection !== 'ALL' && <>
                    <span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    background: "#E3F2FD",
                    color: "#0D47A1",
                    padding: "6px 12px",
                    borderRadius: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4
                  }}>
                      <i className="fas fa-users"></i> Section {selSection}
                    </span>
                    <button onClick={() => window.print()} style={{
                    fontSize: 12,
                    fontWeight: 700,
                    background: "#0D47A1",
                    color: "#FFFFFF",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer"
                  }}>
                      <i className="fas fa-print"></i> Print
                    </button>
                    <button onClick={() => navigate(`/student-analysis?cls=${encodeURIComponent(selClass)}&section=${encodeURIComponent(selSection)}&group=${encodeURIComponent(selGroup || '')}`)} style={{
                    fontSize: 12,
                    fontWeight: 700,
                    background: "#FFFFFF",
                    color: "#0D47A1",
                    border: "1.5px solid #0D47A1",
                    padding: "6px 14px",
                    borderRadius: 20,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    cursor: "pointer"
                  }}>
                      <i className="fas fa-chart-line"></i> Student Analysis
                    </button>
                  </>}
              </div>
            </div>
            <div style={{
              fontSize: 12,
              color: "#2196F3"
            }}>
              Showing {displayedStudents.length} of {allStudents.filter(s => s.cls === selClass).length} students
            </div>
          </div>

          {/* Section-level summary + fee table — this part only is printed */}
          <div className="fee-print-area">
          <div style={{
              fontSize: 14,
              fontWeight: 700,
              color: "#0D47A1",
              marginBottom: 8,
              display: "none"
            }} className="print-only-heading">
            {selClass}{selGroup && selGroup !== 'ALL' ? ` · ${selGroup}` : ''} · Section {selSection} — Fee Details
          </div>
          {(() => {
              const secStudents = allStudents.filter(s => s.cls === selClass && s.section === selSection);
              const collected = secStudents.reduce((a, x) => a + x.paid, 0);
              const pending = secStudents.reduce((a, x) => a + x.pendingTerm, 0);
              const completed = secStudents.filter(s => s.pendingTerm === 0).length;
              return <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: 12,
                marginBottom: 20
              }}>
                <div style={{
                  background: "#E3F2FD",
                  borderRadius: 12,
                  padding: "10px 12px"
                }}>
                  <div style={{
                    fontSize: 10,
                    color: "#2196F3"
                  }}><i className="fas fa-coins"></i> Collected</div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0D47A1"
                  }}>₹{collected.toLocaleString()}</div>
                </div>
                <div style={{
                  background: "#E3F2FD",
                  borderRadius: 12,
                  padding: "10px 12px"
                }}>
                  <div style={{
                    fontSize: 10,
                    color: "#2196F3"
                  }}><i className="fas fa-hourglass-half"></i> Pending</div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0D47A1"
                  }}>₹{pending.toLocaleString()}</div>
                </div>
                <div style={{
                  background: "#E3F2FD",
                  borderRadius: 12,
                  padding: "10px 12px"
                }}>
                  <div style={{
                    fontSize: 10,
                    color: "#2196F3"
                  }}><i className="fas fa-check-circle"></i> Completed</div>
                  <div style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#0D47A1"
                  }}>{completed}</div>
                </div>
              </div>;
            })()}

          <div style={{
              overflowX: "auto"
            }}>
            <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 15,
                minWidth: 1000
              }}>
              <thead>
                <tr style={{
                    background: "#E3F2FD"
                  }}>
                  {['#', 'Roll', 'Student', 'Total Fee', 'Paid', 'Pending', 'T1', 'T2', 'T3', 'Actions'].map(h => <th key={h} style={{
                      textAlign: "left",
                      padding: "12px 12px",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "#0D47A1",
                      textTransform: "uppercase",
                      letterSpacing: 0.5
                    }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {displayedStudents.map((s, i) => <tr key={s.id} style={{
                    borderBottom: "1px solid #E3F2FD"
                  }}>
                    <td style={{
                      padding: "12px 12px",
                      color: "#2196F3"
                    }}>{i + 1}</td>
                    <td style={{
                      padding: "12px 12px",
                      fontWeight: 500,
                      color: "#2196F3"
                    }}>{s.roll}</td>
                    <td style={{
                      padding: "12px 12px"
                    }}>
                      <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                      }}>
                        <div style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "linear-gradient(135deg, #0D47A1, #2196F3)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          fontSize: 14,
                          fontWeight: 600
                        }}>{s.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                        <div>
                          <div style={{
                            fontWeight: 600,
                            color: "#0D47A1",
                            fontSize: 15
                          }}>{s.name}</div>
                          <div style={{
                            fontSize: 11,
                            color: "#2196F3"
                          }}>Roll {s.roll}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{
                      padding: "12px 12px",
                      fontWeight: 500,
                      color: "#0D47A1"
                    }}>₹{s.totalFee.toLocaleString()}</td>
                    <td style={{
                      padding: "12px 12px",
                      fontWeight: 500,
                      color: "#2196F3"
                    }}>₹{s.paid.toLocaleString()}</td>
                    <td style={{
                      padding: "12px 12px",
                      fontWeight: 600,
                      color: "#0D47A1"
                    }}>₹{s.pending.toLocaleString()}</td>
                    {['T1', 'T2', 'T3'].map(t => {
                      const term = s.termFees[t];
                      const isPaid = term.pending === 0;
                      const isPartial = term.paid > 0 && term.pending > 0;
                      const isUnpaid = term.paid === 0;
                      return <td key={t} style={{
                        padding: "12px 10px",
                        verticalAlign: "middle"
                      }}>
                          <div style={{
                          background: isPaid ? "#E3F2FD" : isPartial ? "#E3F2FD" : "#FFFFFF",
                          borderRadius: 14,
                          padding: "10px 12px",
                          minWidth: 150,
                          boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
                        }}>
                            {/* Status Pill */}
                            <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 6
                          }}>
                              <span style={{
                              fontSize: 13,
                              fontWeight: 700,
                              color: "#0D47A1"
                            }}>{t}</span>
                              <span style={{
                              fontSize: 11,
                              fontWeight: 700,
                              textTransform: "uppercase",
                              color: isPaid ? "#2196F3" : isPartial ? "#0D47A1" : "#2196F3",
                              background: isPaid ? "#FFFFFF" : isPartial ? "#FFFFFF" : "#E3F2FD",
                              padding: "2px 8px",
                              borderRadius: 20
                            }}>
                                {isPaid ? "Paid" : isPartial ? "Partial" : "Unpaid"}
                              </span>
                            </div>
                            
                            {/* Amount details */}
                            <div style={{
                            fontSize: 13,
                            color: "#0D47A1",
                            lineHeight: 1.4
                          }}>
                              <div>Due: <span style={{
                                fontWeight: 600
                              }}>₹{term.due.toLocaleString()}</span></div>
                              {term.paid > 0 && <div>Paid: <span style={{
                                fontWeight: 600,
                                color: "#2196F3"
                              }}>₹{term.paid.toLocaleString()}</span></div>}
                              {term.pending > 0 && <div>Pend: <span style={{
                                fontWeight: 600,
                                color: "#0D47A1"
                              }}>₹{term.pending.toLocaleString()}</span></div>}
                            </div>

                            {/* Buttons */}
                            <div style={{
                            display: "flex",
                            gap: 6,
                            marginTop: 8
                          }}>
                              {!isPaid ? <button onClick={() => openTermPaymentModal(s.id, t)} style={{
                              flex: 1,
                              background: "#0D47A1",
                              border: "none",
                              borderRadius: 30,
                              padding: "6px 8px",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: "pointer",
                              transition: "background 0.2s"
                            }}>Pay</button> : <div style={{
                              flex: 1,
                              textAlign: "center",
                              color: "#2196F3",
                              fontSize: 12,
                              fontWeight: 600,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: 4,
                              padding: "6px 0"
                            }}>
                                  <i className="fas fa-check-circle"></i> Paid
                                </div>}
                              <button onClick={() => handleEditTermDue(s.id, t)} style={{
                              background: "#FFFFFF",
                              borderRadius: 30,
                              padding: "6px 8px",
                              color: "#0D47A1",
                              fontSize: 12,
                              fontWeight: 500,
                              cursor: "pointer",
                              boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
                            }}>Edit</button>
                            </div>
                          </div>
                        </td>;
                    })}
                    <td style={{
                      padding: "12px 12px"
                    }}>
                      <button onClick={() => openStudent(s)} style={{
                        background: "#E3F2FD",
                        border: "none",
                        borderRadius: 30,
                        padding: "6px 14px",
                        fontSize: 13,
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        color: "#0D47A1"
                      }}><i className="fas fa-eye"></i> View</button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
            {displayedStudents.length === 0 && <div style={{
                textAlign: "center",
                padding: 48,
                color: "#2196F3"
              }}>
                <i className="fas fa-user-slash"></i> No students found
              </div>}
          </div>
          </div>
        </div>
      </div>

      {/* ── Bulk fee modal (blue theme) ── */}
      {showBulkFeeModal && <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,71,161,0.5)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
      }}>
          <div style={{
          background: "#fff",
          borderRadius: 24,
          width: "100%",
          maxWidth: 760,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 24,
          boxShadow: "0 20px 40px rgba(13,71,161,0.1)"
        }}>
            <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 20
          }}>
              <div>
                <div style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0D47A1"
              }}>Set Fees — {selClass || 'Class'}</div>
                <div style={{
                fontSize: 12,
                color: "#2196F3"
              }}>Apply to: <span style={{
                  fontWeight: 600,
                  color: "#0D47A1"
                }}>{applyScope === 'class' ? 'Whole Class' : 'Specific Group'}</span></div>
                <div style={{
                fontSize: 11.5,
                color: "#607D8B",
                marginTop: 2
              }}>Day Scholars get Van Fee · Hostel Students (registered in the hostel) get Hostel Fee instead — School/Book/Tuition Fee apply to both.</div>
              </div>
              <button onClick={() => setShowBulkFeeModal(false)} style={{
              background: "none",
              border: "none",
              fontSize: 20,
              cursor: "pointer",
              color: "#2196F3"
            }}><i className="fas fa-times"></i></button>
            </div>

            <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: 16
          }}>
              <div>
                <label style={{
                fontSize: 11,
                color: "#2196F3"
              }}>School Fee</label>
                <input value={compSchoolFee} onChange={e => {
                const v = e.target.value;
                setCompSchoolFee(v);
                const n = parseInt((v || '').toString().replace(/[^0-9]/g, ''), 10) || 0;
                if (n > 0) {
                  setCompTuitionFee('');
                }
              }} placeholder="e.g. 12000" style={{
                width: "100%",
                borderRadius: 10,
                padding: "8px 12px",
                marginTop: 4,
                fontSize: 13,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
              </div>
              <div>
                <label style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Van Fee <span style={{
                  color: "#90CAF9"
                }}>(Day Scholars)</span></label>
                <input value={compVanFee} onChange={e => setCompVanFee(e.target.value)} placeholder="e.g. 2000" style={{
                width: "100%",
                borderRadius: 10,
                padding: "8px 12px",
                marginTop: 4,
                fontSize: 13,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
              </div>
              <div>
                <label style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Hostel Fee <span style={{
                  color: "#90CAF9"
                }}>(Hostel Students)</span></label>
                <input value={compHostelFee} onChange={e => setCompHostelFee(e.target.value)} placeholder="e.g. 15000" style={{
                width: "100%",
                borderRadius: 10,
                padding: "8px 12px",
                marginTop: 4,
                fontSize: 13,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
              </div>
              <div>
                <label style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Book Fee</label>
                <input value={compBookFee} onChange={e => setCompBookFee(e.target.value)} placeholder="e.g. 1000" style={{
                width: "100%",
                borderRadius: 10,
                padding: "8px 12px",
                marginTop: 4,
                fontSize: 13,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
              </div>
              <div>
                <label style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Tuition Fee</label>
                <input value={compTuitionFee} onChange={e => {
                const v = e.target.value;
                setCompTuitionFee(v);
                const n = parseInt((v || '').toString().replace(/[^0-9]/g, ''), 10) || 0;
                if (n > 0) {
                  setCompSchoolFee('');
                }
              }} placeholder="e.g. 9000" style={{
                width: "100%",
                borderRadius: 10,
                padding: "8px 12px",
                marginTop: 4,
                fontSize: 13,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
              </div>
            </div>

            <div style={{
            marginTop: 16
          }}>
              <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: "#0D47A1",
              marginBottom: 8
            }}>Other components</div>
              <div style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap"
            }}>
                <input value={otherLabel} onChange={e => setOtherLabel(e.target.value)} placeholder="Label" style={{
                flex: 1,
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
                <input value={otherAmount} onChange={e => setOtherAmount(e.target.value)} placeholder="Amount" style={{
                width: 120,
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: 12,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }} />
                <button onClick={() => {
                const amt = parseInt((otherAmount || '').toString().replace(/[^0-9]/g, ''), 10) || 0;
                if (!otherLabel || amt <= 0) return alert('Enter label and amount');
                setCompOthers(prev => [...prev, {
                  label: otherLabel,
                  amount: amt
                }]);
                setOtherLabel('');
                setOtherAmount('');
              }} style={{
                background: "#E3F2FD",
                border: "none",
                borderRadius: 10,
                padding: "0 16px",
                cursor: "pointer",
                color: "#0D47A1"
              }}><i className="fas fa-plus"></i> Add</button>
              </div>
              <div style={{
              marginTop: 12
            }}>
                {compOthers.map((o, i) => <div key={i} style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: "#E3F2FD",
                padding: "8px 12px",
                borderRadius: 10,
                marginBottom: 6
              }}>
                    <span style={{
                  fontSize: 13,
                  color: "#0D47A1"
                }}>{o.label}</span>
                    <div style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12
                }}>
                      <span style={{
                    fontWeight: 600,
                    color: "#0D47A1"
                  }}>₹{o.amount.toLocaleString()}</span>
                      <button onClick={() => setCompOthers(prev => prev.filter((_, j) => j !== i))} style={{
                    background: "none",
                    border: "none",
                    color: "#0D47A1",
                    cursor: "pointer"
                  }}><i className="fas fa-trash-alt"></i></button>
                    </div>
                  </div>)}
              </div>
            </div>

            <div style={{
            marginTop: 20,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12
          }}>
              <div style={{
              fontSize: 12,
              color: "#2196F3",
              lineHeight: 1.6
            }}>
                {(() => {
                const s = parseInt((compSchoolFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const v = parseInt((compVanFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const hf = parseInt((compHostelFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const b = parseInt((compBookFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const t = parseInt((compTuitionFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const o = compOthers.reduce((a, x) => a + (Number(x.amount) || 0), 0);
                const dayTotal = s + v + b + t + o;
                const hostelTotal = s + hf + b + t + o;
                return <>
                      <div>Day Scholars: <strong style={{
                      color: "#0D47A1"
                    }}>₹{dayTotal.toLocaleString()}</strong></div>
                      <div>Hostel Students: <strong style={{
                      color: "#0D47A1"
                    }}>₹{hostelTotal.toLocaleString()}</strong></div>
                    </>;
              })()}
              </div>
              <div style={{
              display: "flex",
              gap: 8,
              alignItems: "center"
            }}>
                {(selClass === 'Class 11' || selClass === 'Class 12') && <select value={applyScope} onChange={e => setApplyScope(e.target.value)} style={{
                borderRadius: 30,
                padding: "6px 12px",
                fontSize: 12,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }}>
                    <option value="class">Whole Class</option>
                    <option value="group">Specific Group</option>
                  </select>}
                {applyScope === 'group' && <select value={selGroup || ''} onChange={e => setSelGroup(e.target.value)} style={{
                borderRadius: 30,
                padding: "6px 12px",
                fontSize: 12,
                boxShadow: "0 2px 6px rgba(13,71,161,0.04)"
              }}>
                    <option value="">Select group</option>
                    {HS_GROUPS.map(g => <option key={g} value={g}>{g}</option>)}
                  </select>}
                <button onClick={async () => {
                const s = parseInt((compSchoolFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const v = parseInt((compVanFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const hf = parseInt((compHostelFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const b = parseInt((compBookFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const t = parseInt((compTuitionFee || '0').toString().replace(/[^0-9]/g, ''), 10) || 0;
                const others = compOthers.map(o => ({
                  label: o.label,
                  amount: Number(o.amount) || 0
                }));
                const dayTotal = s + v + b + t + others.reduce((a, x) => a + (x.amount || 0), 0);
                const hostelTotal = s + hf + b + t + others.reduce((a, x) => a + (x.amount || 0), 0);
                if (dayTotal <= 0 && hostelTotal <= 0) return alert('Enter at least one fee component');
                const payload = {
                  className: selClass,
                  group: applyScope === 'group' ? selGroup : undefined,
                  dayScholarComponents: {
                    schoolFee: s,
                    vanFee: v,
                    bookFee: b,
                    tuitionFee: t,
                    others
                  },
                  hostelComponents: {
                    schoolFee: s,
                    vanFee: 0,
                    bookFee: b,
                    tuitionFee: t,
                    others: hf > 0 ? [...others, {
                      label: 'Hostel Fee',
                      amount: hf
                    }] : others
                  }
                };
                try {
                  const res = await fetch('https://schoolcrm-fzcv.onrender.com/api/fees/set-bulk', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                  });
                  if (!res.ok) throw new Error('Failed to set bulk fees');
                  const data = await res.json();
                  alert(`Applied fees to ${data.count} students (${data.dayScholarCount || 0} Day Scholars, ${data.hostelCount || 0} Hostel Students)`);
                  setShowBulkFeeModal(false);
                  await loadAdmissions();
                } catch (err) {
                  console.error(err);
                  alert('Failed to apply fees — see console');
                }
              }} style={{
                background: "#0D47A1",
                border: "none",
                borderRadius: 40,
                padding: "8px 20px",
                fontWeight: 600,
                color: "#FFFFFF",
                cursor: "pointer"
              }}>Apply</button>
              </div>
            </div>
          </div>
        </div>}

      {/* ── Per-component ("School Fee / Van Fee / Book Fee / Tuition Fee / Others")
           Term Payment modal ── */}
      {termPaymentModal && (() => {
        const student = allStudents.find(s => s.id === termPaymentModal.studentId);
        if (!student) return null;
        const termKey = termPaymentModal.termKey;
        const rows = getComponentRows(student, termKey);
        return <div style={{
          position: "fixed",
          inset: 0,
          background: "rgba(13,71,161,0.5)",
          zIndex: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 16
        }}>
            <div style={{
            background: "#fff",
            borderRadius: 24,
            width: "100%",
            maxWidth: 640,
            maxHeight: "90vh",
            overflowY: "auto",
            padding: 24,
            boxShadow: "0 20px 40px rgba(13,71,161,0.1)"
          }}>
              <div style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              marginBottom: 8
            }}>
                <div>
                  <div style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#0D47A1"
                }}>{student.name} · {termKey} Payment</div>
                  <div style={{
                  fontSize: 12,
                  color: "#2196F3",
                  marginTop: 2
                }}>{student.cls}{student.group ? ` • ${student.group}` : ''} • Section {student.section}</div>
                </div>
                <button onClick={() => setTermPaymentModal(null)} style={{
                background: "none",
                border: "none",
                fontSize: 18,
                cursor: "pointer",
                color: "#2196F3"
              }}><i className="fas fa-times"></i></button>
              </div>

              <div style={{
              fontSize: 12,
              color: "#2196F3",
              marginBottom: 16
            }}>
                Pay each fee head separately — School Fee, Tuition Fee, Van Fee, Book Fee and any Other charges.
              </div>

              {rows.length === 0 && <div style={{
              textAlign: "center",
              padding: 24,
              color: "#2196F3",
              background: "#E3F2FD",
              borderRadius: 16
            }}>
                  No fee components set for {termKey} yet. Use <strong>Set Class Fees</strong> or the <strong>Edit</strong> button to add School / Van / Book / Tuition amounts first.
                </div>}

              <div style={{
              display: "flex",
              flexDirection: "column",
              gap: 12
            }}>
                {rows.map((row, idx) => {
                const pending = Math.max(0, row.due - row.paid);
                const isPaid = pending === 0;
                const isPartial = row.paid > 0 && pending > 0;
                const inputKey = `${termKey}-${row.key}-${row.label}`;
                return <div key={idx} style={{
                  background: "#E3F2FD",
                  borderRadius: 16,
                  padding: "14px 16px"
                }}>
                      <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 8
                  }}>
                        <span style={{
                      fontSize: 14,
                      fontWeight: 700,
                      color: "#0D47A1"
                    }}>{row.label}</span>
                        <span style={{
                      fontSize: 11,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      color: isPaid ? "#2196F3" : "#0D47A1",
                      background: "#fff",
                      padding: "2px 8px",
                      borderRadius: 20
                    }}>
                          {isPaid ? "Paid" : isPartial ? "Partial" : "Unpaid"}
                        </span>
                      </div>

                      <div style={{
                    display: "flex",
                    gap: 16,
                    flexWrap: "wrap",
                    fontSize: 12,
                    color: "#0D47A1",
                    marginBottom: 10
                  }}>
                        <span>Due: <strong>₹{row.due.toLocaleString()}</strong></span>
                        <span>Paid: <strong style={{
                        color: "#2196F3"
                      }}>₹{row.paid.toLocaleString()}</strong></span>
                        <span>Pending: <strong>₹{pending.toLocaleString()}</strong></span>
                      </div>

                      {!isPaid ? <div style={{
                    display: "flex",
                    gap: 8
                  }}>
                          <input value={paymentInputs[inputKey] ?? ''} onChange={e => setPaymentInputs(prev => ({
                      ...prev,
                      [inputKey]: e.target.value
                    }))} placeholder={`${pending}`} style={{
                      flex: 1,
                      borderRadius: 10,
                      padding: "8px 12px",
                      fontSize: 13,
                      border: "none",
                      boxShadow: "0 2px 6px rgba(13,71,161,0.08)"
                    }} />
                          <button onClick={() => payComponent(row)} style={{
                      background: "#0D47A1",
                      color: "#fff",
                      border: "none",
                      borderRadius: 10,
                      padding: "0 18px",
                      fontWeight: 600,
                      fontSize: 13,
                      cursor: "pointer"
                    }}>Pay</button>
                        </div> : <div style={{
                    color: "#2196F3",
                    fontSize: 12,
                    fontWeight: 600
                  }}><i className="fas fa-check-circle"></i> Fully paid</div>}
                    </div>;
              })}
              </div>
            </div>
          </div>;
      })()}

      {/* ── Student detail modal ── */}
      {modalStudent && <div style={{
        position: "fixed",
        inset: 0,
        background: "rgba(13,71,161,0.5)",
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16
      }}>
          <div style={{
          background: "#fff",
          borderRadius: 24,
          width: "100%",
          maxWidth: 880,
          maxHeight: "90vh",
          overflowY: "auto",
          padding: 24,
          boxShadow: "0 20px 40px rgba(13,71,161,0.1)"
        }}>
            <div style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            marginBottom: 16
          }}>
              <div>
                <div style={{
                fontSize: 18,
                fontWeight: 700,
                color: "#0D47A1"
              }}>{modalStudent.name} · Roll {modalStudent.roll}</div>
                <div style={{
                fontSize: 13,
                color: "#2196F3",
                marginTop: 2
              }}>{modalStudent.cls}{modalStudent.group ? ` • ${modalStudent.group}` : ''} • Section {modalStudent.section}</div>
              </div>
              <button onClick={() => setModalStudent(null)} style={{
              background: "none",
              border: "none",
              fontSize: 18,
              cursor: "pointer",
              color: "#2196F3"
            }}><i className="fas fa-times"></i></button>
            </div>

            <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
            marginBottom: 20
          }}>
              <div style={{
              background: "#E3F2FD",
              borderRadius: 16,
              padding: "12px 16px"
            }}>
                <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Total Fee</div>
                <div style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#0D47A1"
              }}>₹{modalStudent.totalFee.toLocaleString()}</div>
              </div>
              <div style={{
              background: "#E3F2FD",
              borderRadius: 16,
              padding: "12px 16px"
            }}>
                <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Paid</div>
                <div style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#2196F3"
              }}>₹{modalStudent.paid.toLocaleString()}</div>
              </div>
              <div style={{
              background: "#E3F2FD",
              borderRadius: 16,
              padding: "12px 16px"
            }}>
                <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}>Pending</div>
                <div style={{
                fontSize: 20,
                fontWeight: 700,
                color: "#0D47A1"
              }}>₹{modalStudent.pendingTerm.toLocaleString()}</div>
              </div>
            </div>

            <div style={{
            marginBottom: 20
          }}>
              <div style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#0D47A1",
              marginBottom: 12
            }}>Term Breakdown</div>
              <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 12
            }}>
                {['T1', 'T2', 'T3'].map(t => {
                const tf = modalStudent.termFees[t];
                const cp = tf.componentsPaid || {
                  schoolFee: 0,
                  vanFee: 0,
                  bookFee: 0,
                  tuitionFee: 0,
                  others: []
                };
                return <div key={t} style={{
                  background: "#E3F2FD",
                  borderRadius: 16,
                  padding: "12px 14px"
                }}>
                      <div style={{
                    fontWeight: 700,
                    color: "#0D47A1",
                    marginBottom: 6
                  }}>{t}</div>
                      <div style={{
                    color: "#0D47A1"
                  }}>Due: ₹{tf.due.toLocaleString()}</div>
                      <div style={{
                    color: "#2196F3"
                  }}>Paid: ₹{tf.paid.toLocaleString()}</div>
                      <div style={{
                    color: "#0D47A1"
                  }}>Pending: ₹{tf.pending.toLocaleString()}</div>
                      <div style={{
                    fontSize: 11,
                    marginTop: 8,
                    color: "#2196F3"
                  }}>
                        <div>Breakdown (Due / Paid):</div>
                        <div style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      marginTop: 4
                    }}>
                          <span style={{
                        color: "#0D47A1"
                      }}>School: ₹{(tf.components.schoolFee || 0).toLocaleString()} / ₹{(cp.schoolFee || 0).toLocaleString()}</span>
                          <span style={{
                        color: "#0D47A1"
                      }}>Tuition: ₹{(tf.components.tuitionFee || 0).toLocaleString()} / ₹{(cp.tuitionFee || 0).toLocaleString()}</span>
                          <span style={{
                        color: "#0D47A1"
                      }}>Van: ₹{(tf.components.vanFee || 0).toLocaleString()} / ₹{(cp.vanFee || 0).toLocaleString()}</span>
                          <span style={{
                        color: "#0D47A1"
                      }}>Book: ₹{(tf.components.bookFee || 0).toLocaleString()} / ₹{(cp.bookFee || 0).toLocaleString()}</span>
                        </div>
                        {Array.isArray(tf.components.others) && tf.components.others.length > 0 && <div style={{
                      marginTop: 4,
                      color: "#0D47A1"
                    }}>
                            Others: {tf.components.others.map(o => {
                        const paidEntry = (cp.others || []).find(x => x.label === o.label);
                        return `${o.label} ₹${(o.amount || 0).toLocaleString()} / ₹${(paidEntry ? paidEntry.amount : 0).toLocaleString()}`;
                      }).join(', ')}
                          </div>}
                      </div>
                    </div>;
              })}
              </div>
            </div>

            <div>
              <div style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12
            }}>
                <div style={{
                fontSize: 14,
                fontWeight: 600,
                color: "#0D47A1"
              }}>Payment History</div>
                <div style={{
                fontSize: 11,
                color: "#2196F3"
              }}>{transactions.filter(t => t.studentId === modalStudent.id).length} records</div>
              </div>
              <div style={{
              maxHeight: 260,
              overflowY: "auto",
              borderTop: "1px solid #E3F2FD"
            }}>
                {transactions.filter(t => t.studentId === modalStudent.id).map(tx => <div key={tx.id} style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 0",
                borderBottom: "1px solid #E3F2FD"
              }}>
                    <div>
                      <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8
                  }}>
                        <span style={{
                      fontWeight: 600,
                      fontSize: 14,
                      color: "#0D47A1"
                    }}>₹{tx.amount.toLocaleString()}</span>
                        <span style={{
                      fontSize: 10,
                      background: "#E3F2FD",
                      padding: "2px 6px",
                      borderRadius: 20,
                      color: "#0D47A1",
                      fontWeight: 600
                    }}>
                          {tx.term}
                        </span>
                        <span style={{
                      fontSize: 10,
                      background: "#E3F2FD",
                      padding: "2px 6px",
                      borderRadius: 20,
                      color: "#0D47A1",
                      fontWeight: 600,
                      textTransform: "uppercase"
                    }}>
                          {tx.method}
                        </span>
                      </div>
                      {tx.note && <div style={{
                    fontSize: 11,
                    color: "#2196F3",
                    marginTop: 4,
                    background: "#E3F2FD",
                    padding: "4px 8px",
                    borderRadius: 8,
                    display: "inline-block"
                  }}>{tx.note}</div>}
                      <div style={{
                    fontSize: 10,
                    color: "#2196F3",
                    marginTop: 4
                  }}><i className="far fa-clock"></i> {tx.dateTime || tx.date}</div>
                    </div>
                    <div style={{
                  textAlign: "right"
                }}>
                      <div style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#2196F3'
                  }}><i className="fas fa-check-circle"></i> Success</div>
                      <div style={{
                    fontSize: 10,
                    color: "#2196F3",
                    marginTop: 2
                  }}>ID: {tx.id}</div>
                    </div>
                  </div>)}
                {transactions.filter(t => t.studentId === modalStudent.id).length === 0 && <div style={{
                textAlign: "center",
                padding: 32,
                color: "#2196F3"
              }}>No payments recorded</div>}
              </div>
            </div>
          </div>
        </div>}
    </div>
    </>;
}